import { Dispatch } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import createAsyncAction from '@Actions/create-async-action';
import { ActionConsts } from '@Definitions/ActionConsts';
import { TimePeriod } from '@Reducers/schedule';
import { ISlotData } from '@Reducers/slot/types';
import { RootState } from '@Redux/store';
import { ScheduleService, TrackService } from '@Services';
import { RedcliffeService } from '@Services/Redcliffe';
import { TrackSlotsPayload } from '@Services/Track/track-payload';
import {
  CONSULTATION_TYPE,
  DEFAULT_LOCATION_CODE,
  ERROR_MESSAGES,
  ONLINE_LOCATION_ID,
  SLOT_SELECTION,
} from '@Utils/constants';
import { getSessionId } from '@Utils/helper';
import { groupSlotsByDate } from '@Utils/Helpers/schedule/helper';
import {
  getSlotsNext2Hours,
  getSlotsSameDay,
  getSlotsForSpecificDay,
  getSlotsNext15Mins,
  getSlotsBetween15MinsTo30Mins,
  getSlotsBetween30MinsTo60Mins,
  getSlotsBetween60MinsTo120Mins,
  getSlotsBetween2HoursTo4Hours,
  getSlotsBetween4HoursTo6Hours,
  getSlotsBetween6HoursToEod,
  getSlotsAfterDay,
  getSlotsTotal,
  flatMapSlotsData,
  getFirstSlot,
  preselectDefaultSlot,
  getAllProviderIds,
} from '@Utils/Helpers/slot.helper';
import { LOG_ERROR } from '@Utils/logger';

import ActionInputs from './payload';

export const SlotActions = {
  PreviewSlots: createAsyncAction({
    initAction: ActionConsts.Slot.GetPreviewSlotsInit,
    successAction: ActionConsts.Slot.GetPreviewSlotsSuccess,
    failAction: ActionConsts.Slot.GetPreviewSlotsFail,

    asyncFn: async (requestPayload: ActionInputs['PreviewSlots']) => {
      const response = await ScheduleService.GetSlots({
        preview: true,
        consultationTypeId: requestPayload.consultationTypeId,
        startDate: dayjs().toISOString(),
        endDate: dayjs().add(1.5, 'hour').toISOString(),
        providerIds: requestPayload.providerId ? [requestPayload.providerId] : [],
        locationId: ONLINE_LOCATION_ID,
      });

      if (response.status === 200) {
        const groupedSlots = groupSlotsByDate(response.data, false, true);
        const instantPreviewSlots = groupedSlots?.[dayjs().format('YYYY-MM-DD')]?.[TimePeriod.Instant];
        return { result: { instantPreviewSlots }, type: 'success' };
      }
      return { result: [], type: 'fail' };
    },
  }),
  GetSlots: createAsyncAction({
    initAction: ActionConsts.Slot.GetSlotsInit,
    successAction: ActionConsts.Slot.GetSlotsSuccess,
    failAction: ActionConsts.Slot.GetSlotsFail,

    asyncFn: async (requestPayload: ActionInputs['GetSlots'], getState: () => RootState) => {
      const response = await ScheduleService.GetSlots(requestPayload);

      if (response.status === 200) {
        //if screening call and online restrict slot visibility
        const isScreeningCallAndOnline =
          getState().common.queryData?.locationCode === DEFAULT_LOCATION_CODE &&
          getState().consultation.consultationType?.consultationCode === CONSULTATION_TYPE.SC &&
          !Boolean(getState().common.queryData?.allowInstant);

        const formattedSlots = groupSlotsByDate(response.data, false, false);

        const todaysSlot = flatMapSlotsData(formattedSlots?.[dayjs().format('YYYY-MM-DD')]);
        const firstSlot = getFirstSlot(formattedSlots);
        const payload: TrackSlotsPayload = {
          availableProviderIds: getAllProviderIds(formattedSlots),
          slotsNext2Hours: getSlotsNext2Hours(todaysSlot),
          slotsSameDay: getSlotsSameDay(todaysSlot),
          slotsNextDay: getSlotsForSpecificDay(formattedSlots, 2), //day 2
          slotsNext15Mins: getSlotsNext15Mins(todaysSlot),
          slotsNext15To30Mins: getSlotsBetween15MinsTo30Mins(todaysSlot),
          slotsNext30To60Mins: getSlotsBetween30MinsTo60Mins(todaysSlot),
          slotsNext60To120Mins: getSlotsBetween60MinsTo120Mins(todaysSlot),
          slotsNext2To4Hours: getSlotsBetween2HoursTo4Hours(todaysSlot),
          slotsNext4To6Hours: getSlotsBetween4HoursTo6Hours(todaysSlot),
          slotsNext6ToEod: getSlotsBetween6HoursToEod(todaysSlot),
          slotsDay3: getSlotsForSpecificDay(formattedSlots, 3), //day 3
          slotsDay4: getSlotsForSpecificDay(formattedSlots, 4), //day 4
          slotsAfterDay4: getSlotsAfterDay(formattedSlots, 4), //beyond day 4
          slotsTotal: getSlotsTotal(formattedSlots),
          speciality: requestPayload?.speciality || 'sexual-wellness',
          eventName: SLOT_SELECTION.DEFAULT,
          consultationTypeId: requestPayload?.consultationTypeId,
          flow: requestPayload?.flow,
          slotTime: firstSlot?.startDate,
          sessionId: getSessionId(),
          blockId: firstSlot?.blockId,
          providerIds: firstSlot?.providerId ? [firstSlot?.providerId] : requestPayload?.providerIds,
          excludedProviderIds: requestPayload?.excludedProviderIds,
          languages: requestPayload?.preferredLanguages,
          locationId: requestPayload?.locationId,
        };
        if (preselectDefaultSlot(getState().consultation.consultationType?.consultationCode)) {
          await TrackService.TrackSlots(payload);
        }
        await TrackService.TrackSlots({
          ...payload,
          eventName: SLOT_SELECTION.SLOTS_LISTED,
          slotTime: undefined,
          blockId: undefined,
          providerIds: requestPayload?.providerIds,
        });
        return {
          result: {
            slotsData: formattedSlots,
          },
          type: 'success',
        };
      } else return { result: [], type: 'fail' };
    },
  }),
  GetProvidersBySlots: createAsyncAction({
    initAction: ActionConsts.Slot.GetProvidersBySlotsInit,
    successAction: ActionConsts.Slot.GetProvidersBySlotsSuccess,
    failAction: ActionConsts.Slot.GetProvidersBySlotsFail,

    asyncFn: async (requestPayload: null, getState: () => RootState) => {
      const slots: ISlotData = getState().slot.activeSlots.slots;
      const providers: Set<string> = new Set();
      for (const [_, slotsForDay] of Object.entries(slots)) {
        for (const [_, slotsForTimePeriod] of Object.entries(slotsForDay)) {
          for (const slot of slotsForTimePeriod) {
            providers.add(slot.providerId);
          }
        }
      }
      return { result: { providers }, type: 'success' };
    },
  }),
  // eslint-disable-next-line unicorn/consistent-function-scoping
  ResetSlots: () => async (dispatch: Dispatch) => {
    dispatch({
      type: ActionConsts.Slot.ResetSlots,
    });
  },
  GetRedcliffeSlots:
    (requestPayload: { id: string; invoiceId: string; eloc: string; collectionDate: string; slotPeriod: number }) =>
    async (dispatch: Dispatch) => {
      try {
        dispatch({
          type: ActionConsts.Slot.GetRedcliffeSlotsInit,
        });
        const response = await RedcliffeService.GetRedcliffeSlots(requestPayload);
        if (response.status === 200) {
          dispatch({
            type: ActionConsts.Slot.GetRedcliffeSlotsSuccess,
            redcliffeSlots: response.data,
          });
          return response.data;
        } else {
          dispatch({
            type: ActionConsts.Slot.GetRedcliffeSlotsFail,
          });
          if (response?.data && 'message' in response.data && response.data.message === ERROR_MESSAGES.location) {
            throw new Error(ERROR_MESSAGES.location);
          }
        }
      } catch (error) {
        dispatch({
          type: ActionConsts.Slot.GetRedcliffeSlotsFail,
        });
        if (error.message === ERROR_MESSAGES.location) {
          throw error;
        } else {
          LOG_ERROR('Error: GetRedcliffeSlots>>SlotActions', error);
        }
      }
    },
  GetDirectRedcliffeSlots:
    (requestPayload: { eloc: string; collectionDate: string; slotPeriod: number }) => async (dispatch: Dispatch) => {
      try {
        dispatch({
          type: ActionConsts.Slot.GetRedcliffeSlotsInit,
        });
        const response = await RedcliffeService.GetDirectRedcliffeSlots(requestPayload);
        if (response.status === 200) {
          dispatch({
            type: ActionConsts.Slot.GetRedcliffeSlotsSuccess,
            redcliffeSlots: response.data,
          });
          return response.data;
        } else {
          dispatch({
            type: ActionConsts.Slot.GetRedcliffeSlotsFail,
          });
          throw new Error(ERROR_MESSAGES.location);
        }
      } catch (error) {
        dispatch({
          type: ActionConsts.Slot.GetRedcliffeSlotsFail,
        });
        if (error.message === ERROR_MESSAGES.location) {
          throw error;
        } else {
          LOG_ERROR('Error: GetRedcliffeSlots>>SlotActions', error);
        }
      }
    },
};
